<template>
	<fieldset class="v-address-lookup">
		<div class="v-address-lookup__legend">
			<legend><span v-html="label"/></legend>
			<ul v-if="!disableLookup && !isSelecting" class="v-address-lookup__legend__actions">
				<li v-if="!isEditing && !isLooking">
					<button
						class="button button--secondary button--small" @click.prevent="editAddress()">Edit
					</button>
				</li>
				<li v-if="!isLooking">
					<button
						class="button button--secondary button--small" @click.prevent="reset()">Lookup
					</button>
				</li>
			</ul>
		</div>
		<div class="input-group">
			<div v-if="isLooking" class="form-input v-address-lookup__postcode mt-05">
				<div class="v-address-lookup__postcode__label">
					<label :for="fieldId">
						<span>Find address by postcode</span>
					</label>
				</div>
				<div class="v-address-lookup__postcode__field">
					<input
						ref="lookupField"
						:name="fieldId"
						:value="address.address_postcode" placeholder="postcode" type="text"
						@blur="unwatchKey('Enter')" @focus="watchKey('Enter', findAddresses)"
						@keyup.prevent="event => formatPostcode(event)"/>
					<button
						:disabled="!postcodeIsValid"
						class="v-address-lookup__postcode__field__button button button--primary--small"
						@click.prevent="findAddresses()">Lookup
					</button>
				</div>
			</div>
			<div v-if="isSelecting" class="v-address-lookup__search">
				<label :for="fieldId + '-search'" class="v-address-lookup__search__label">
					<span>Lookup address for {{ address.address_postcode }}</span>
				</label>
				<div class="v-address-lookup__search__list">
					<input
						ref="search"
						v-model="search" :name="fieldId + '-search'" class="v-address-lookup__search__list__filter"
						placeholder="type to filter addresses"
						type="text"
						@keyup.down="selectAddress()"
						@keyup.up="selectAddress(true)"
						@keydown.enter.prevent="chooseAddress()"/>
					<ul
						v-if="addresses.length" class="v-address-lookup__postcode__address-list"
						data-address-list>
						<li
							v-for="(address, addressIndex) in filteredAddresses" :key="addressIndex"
							:class="addressClass(addressIndex)"
							:data-address="addressIndex"
							@click="getFullAddress(address)"
							v-html="address.Text"/>
						<li @click.prevent="editAddress()">Enter another address</li>
					</ul>
				</div>
			</div>
			<div
				v-if="!hasAddress && !isEditing && hasAnyError"
				class="error-block">
				<p v-if="hasError('address_postcode')" v-html="getError('address_postcode')"/>
				<p v-else>Please provide the address.</p>
			</div>
			<Address
				v-if="hasAddress" :address="address" :class="selectedAddressClass" class="v-address-lookup__address"/>
			<div class="v-address-lookup__edit input-group">
				<template v-if="isEditing">
					<div v-if="showNoAddressesWarning" class="alert alert--warning mt-05">
						<p>No addresses found for {{ address.address_postcode }}. Please enter the address manually.</p>
					</div>
					<label v-if="showField('address_identifier')"><span>Name or number <span
						v-if="isOptional('address_identifier')" class="optional">— optional </span></span>
						<input v-model="address.address_identifier" type="text">
						<div
							v-if="hasError('address_identifier')"
							class="error-block">
							<p v-html="getError('address_identifier')"/>
						</div>
					</label>
					<label v-if="showField('address_street')"><span>Street <span
						v-if="isOptional('address_street')" class="optional">— optional </span></span>
						<input v-model="address.address_street" type="text">
						<div
							v-if="hasError('address_street')"
							class="error-block">
							<p v-html="getError('address_street')"/>
						</div>
					</label>
					<label v-if="showField('address_locality')"><span>Locality/neighborhood <span
						v-if="isOptional('address_locality')" class="optional">— optional </span></span>
						<input v-model="address.address_locality" type="text">
						<div
							v-if="hasError('address_locality')"
							class="error-block">
							<p v-html="getError('address_locality')"/>
						</div>
					</label>
					<label v-if="showField('address_city')"><span>Town/city <span
						v-if="isOptional('address_city')" class="optional">—
						optional </span></span>
						<input v-model="address.address_city" type="text">
						<div v-if="hasError('address_city')" class="error-block">
							<p v-html="getError('address_city')"/>
						</div>
					</label>
					<label v-if="showField('address_province')"><span>County <span
						v-if="isOptional('address_province')" class="optional">— optional </span></span>
						<input v-model="address.address_province" type="text">
						<div
							v-if="hasError('address_province')"
							class="error-block">
							<p v-html="getError('address_province')"/>
						</div>
					</label>
					<label><span>Postcode </span>
						<input
							:value="address.address_postcode"
							type="text"
							@keyup.prevent="event => formatPostcode(event)"/>
						<div
							v-if="hasError('address_postcode')"
							class="error-block">
							<p v-html="getError('address_postcode')"/>
						</div>
					</label>
				</template>
				<div v-if="includeCountry">
					<label><span>Country </span>
						<select
							v-model="address.address_country"
							:disabled="!postcodeIsValid || !selectCountryManually">
							<option disabled value="">—</option>
							<option value="England">England</option>
							<option value="Scotland">Scotland</option>
							<option value="Wales">Wales</option>
						</select>
						<div class="help-block">
							<p v-if="!postcodeIsValid">If possible, this will be set automatically based on the postcode.</p>
							<p v-if="postcodeIsValid && selectCountryManually && !address.address_country">No country found for {{ address.address_postcode }}. Please select manually.</p>
						</div>
						<div
							v-if="hasError('address_country')"
							class="error-block">
							<p v-html="getError('address_country')"/>
						</div>
					</label>
					<!--						<p>No country found for {{ address.address_postcode }}. Please select manually.</p>-->
				</div>
			</div>
		</div>
	</fieldset>
</template>
<script>
import Address from "../../Address";

const initialState = (data = {}) => {
	return _.merge(data, {
		address: {
			address_identifier: "",
			address_street: "",
			address_locality: "",
			address_city: "",
			address_province: "",
			address_postcode: "",
			address_country_code: "",
			address_country: "",
		},
		action: "looking",
		normalizedPostcode: "",
		search: "",
		selectedAddressIndex: null,
		selectCountryManually: false,
		showNoAddressesWarning: false,
	});
};

export default {
	components: {
		Address,
	},
	data() {
		return initialState({
			addresses: [],
			lastPostcode: "",
			// keyBindings: {},
		});
	},

	computed: {
		isLooking() {
			return this.action === "looking";
		},
		isSelecting() {
			return this.action === "selecting";
		},
		isEditing() {
			return this.action === "editing";
		},
		hasAddress() {
			return this.action === null;
		},
		filteredAddresses() {
			return _.filter(this.addresses, (address) => {
				return address.Text.startsWith(this.search);
			});
		},
		selectedAddress() {
			return this.filteredAddresses?.[this.selectedAddressIndex];
		},
		hasAddressComponents() {
			return _.filter(Object.values(this.address), (part) => {
				return part;
			}).length > 0;
		},
		addressComplete() {
			if (!this.address) {
				return false;
			}
			return _.filter(this.requiredParts, (part) => {
				return !this.address[part];
			}).length === 0;
		},
		requiredParts() {
			return _.filter(Object.keys(this.address), (part) => {
				if (part === "address_country") {
					return this.includeCountry;
				}
				return this.optionalFields.hasOwnProperty(part) ? this.optionalFields.part : true;
			});
		},
		hasAnyError() {
			return Object.keys(this.errors).length > 0;
		},
		showAddress() {
			return !this.disableLookup && this.address.address_postcode && !this.isEditing && !this.showAddressList;
		},
		selectedAddressClass() {
			return {
				"v-address-lookup__address--complete": this.addressComplete,
			};
		},
		postcodeIsValid() {
			let pattern = new RegExp("^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) {0,1}[0-9][A-Za-z]{2})$");
			return pattern.test(this.address.address_postcode);
		},
	},

	props: {
		value: {
			required: true,
			type: Object,
		},
		label: {
			required: false,
			type: String,
			default() {
				return "Address";
			},
		},
		fieldId: {
			required: false,
			type: String,
			default() {
				return "";
			},
		},
		disableLookup: {
			required: false,
			type: Boolean,
			default() {
				return false;
			},
		},
		optionalFields: {
			required: false,
			type: Object,
			default() {
				return {
					"address_locality": true,
					"address_city": true,
					"address_country_code": true,
				};
			},
		},
		hideOptionalFields: {
			required: false,
			type: Boolean,
			default() {
				return false;
			},
		},
		includeCountry: {
			required: false,
			type: Boolean,
			default() {
				return true;
			},
		},
		prefill: {
			required: false,
			type: Object,
			default() {
				return {};
			},
		},
		errors: {
			required: false,
			type: Object,
			default() {
				return {};
			},
		},
	},

	methods: {
		reset(usePrefill = false) {
			Object.assign(this.$data, initialState(this.$data));
			this.action = !this.disableLookup ? "looking" : "editing";
			setTimeout(() => {
				this.refs.lookupField?.focus();
			}, 10);
			if (!usePrefill) {
				return;
			}
			this.address = {
				address_identifier: this.prefill?.address_identifier || "",
				address_street: this.prefill?.address_street || "",
				address_locality: this.prefill?.address_locality || "",
				address_city: this.prefill?.address_city || "",
				address_province: this.prefill?.address_province || "",
				address_postcode: this.prefill?.address_postcode?.formatted || this.prefill?.address_postcode || "",
				address_country: this.prefill?.address_country || "",
				address_country_code: this.prefill?.address_country_code || "",
			};
			if (this.hasAddressComponents) {
				this.action = !this.disableLookup ? null : "editing";
			}
		},
		editAddress(showNoAddressesWarning = false) {
			this.action = "editing";
			this.showNoAddressesWarning = showNoAddressesWarning;
		},
		lookup() {
			this.action = "lookup";
		},
		addressClass(addressIndex) {
			return {
				"v-address-lookup__postcode__address-list__address--selected": this.selectedAddressIndex === addressIndex,
			};
		},
		escapeFromAddressList() {
			this.reset(true);
		},
		showField(field) {
			return !this.hideOptionalFields ? true : !this.isOptional(field);
		},
		isOptional(field) {
			if (_.has(this.optionalFields, field)) {
				return this.optionalFields[field];
			}

			return this.optional;
		},
		formatPostcode(event) {
			this.address.address_postcode = event.target.value.toUpperCase();
			event.target.value = this.address.address_postcode;
		},
		normalizePostcode(postcode) {
			return postcode.replace(/\s/g, "").toLowerCase();
		},
		findAddresses() {
			if (!this.postcodeIsValid) {
				return;
			}
			if (this.normalizePostcode(this.address.address_postcode) === this.lastPostcode) {
				return this.showAddressSelector();
			}
			this.lastPostcode = this.normalizedPostcode;
			this.addresses = [];
			let params = {
				Key: "BA11-JZ92-TC14-MJ19",
				Text: this.normalizedPostcode,
				Countries: "GBR",
				Limit: 10,
			};
			externalHttp().post("https://api.addressy.com/Capture/Interactive/Find/v1.1/json3.ws", null, {
				params: params,
			}).then(({data}) => {
				this.addresses = _.filter(data.Items, item => {
					return item.Type === "Address";
				});
				// if (!this.addresses.length) {
				// 	return this.editAddress(true);
				// }
				this.showAddressSelector();
				this.selectedAddressIndex = 0;
			});
		},
		showAddressSelector() {
			if (!this.addresses.length) {
				return this.editAddress(true);
			}
			this.action = "selecting";
			// this.setKeyBinding("Escape", "catchEscape");
			// this.watchKey("Escape", this.escapeFromAddressList);
			setTimeout(() => {
				this.$refs.search.focus();
			}, 10);
		},
		selectAddress(previous = false) {
			let maxIndex = this.filteredAddresses.length - 1;
			this.selectedAddressIndex = previous ? Math.max(this.selectedAddressIndex - 1, 0) : Math.min(this.selectedAddressIndex + 1, maxIndex);
			let $ul = this.$el.querySelector("[data-address-list]");
			let $li = this.$el.querySelector("[data-address=\"" + this.selectedAddressIndex + "\"]");
			let max = $ul.getBoundingClientRect().height;
			let topPos = $li.offsetTop - $ul.scrollTop;
			let bottomPos = topPos + $li.getBoundingClientRect().height;
			if (topPos < 0) {
				$ul.scrollTo(0, $li.offsetTop);
			}
			if (bottomPos > max) {
				$ul.scrollTo(0, $li.offsetTop - max + $li.getBoundingClientRect().height);
			}
		},
		chooseAddress() {
			if (!this.selectedAddress) {
				return;
			}
			this.getFullAddress(this.selectedAddress);
		},
		getFullAddress(address) {
			let params = {
				Key: "BA11-JZ92-TC14-MJ19",
				Id: address.Id,
			};
			externalHttp().post("https://api.addressy.com/Capture/Interactive/Retrieve/v1.2/json3.ws", null, {
				params: params,
			}).then(({data}) => {
				let address = data.Items[0];
				this.address.address_identifier = address.BuildingNumber || address.BuildingName;
				this.address.address_street = address.Street;
				this.address.address_locality = address.Neighbourhood;
				this.address.address_city = address.City;
				this.address.address_province = address.Province || address.AdminAreaName;
				this.address.address_postcode = address.PostalCode;
				this.address.address_country_code = address.CountryIsoNumber;
				this.$emit("addressSelected", this.address);
				this.action = null;
				this.selectedAddressIndex = null;
				this.search = "";
			});
		},
		getCountry() {
			return new Promise((resolve, reject) => {
				if (!this.includeCountry) {
					return resolve();
				}
				let pattern = new RegExp("^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) {0,1}[0-9][A-Za-z]{2})$");
				if (!this.postcodeIsValid) {
					this.address.address_country = "";
					return resolve();
				}
				externalHttp().get("https://api.postcodes.io/postcodes/" + this.address.address_postcode).then(response => {
					this.address.address_country = response.data.result.country;
					this.selectCountryManually = !this.address.address_country;
					resolve();
				}).catch(error => {
					this.address.address_country = "";
					this.selectCountryManually = true;
					resolve();
				});
			});
		},
		hasError(key) {
			return !!this.errors?.[key] || null;
		},
		getError(key) {
			return this.errors[key];
		},
	},

	watch: {
		action: {
			// immediate: true
			handler() {
				this.unwatchKeys();
				switch (this.action) {
					case "looking":
						this.watchKey("Enter", this.findAddresses);
						break;
					case "selecting":
						this.watchKey("Escape", this.escapeFromAddressList);
						this.watchKey("Enter", this.selectAddress);
						break;
				}
			},
		},
		address: {
			deep: true,
			handler() {
				this.normalizedPostcode = this.normalizePostcode(this.address.address_postcode);
				this.$emit("addressUpdated", this.address);
				this.$emit("addressCompleteStatus", this.addressComplete);
				this.$emit("input", this.address);
				this.$emit("change", this.address);
			},
		},
		"address.address_postcode": {
			handler() {
				this.address.address_country = "";
				this.getCountry().then(() => {
					this.$emit("addressUpdated", this.address);
					this.$emit("addressCompleteStatus", this.addressComplete);
				});
			},
		},
		addressComplete: {
			handler: function () {
				this.$emit("addressCompleteStatus", this.addressComplete);
			},
		},
	},

	mounted() {
		this.reset(true);
	},

	destroyed() {
		// this.unwatchKeys(['Enter']);
	},
};
</script>
